import type { LandingTopDonor } from '@/lib/landing-top-donors'

export type RankingPositionOffer = {
  rank: number
  occupied: boolean
  currentAmountCents: number | null
  differenceCents: number
  quantity: number
  amountCents: number
  projectedTotalCents: number
  status: 'available' | 'already' | 'over-limit'
}

function moneyToCents(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount < 0) return null
  const cents = Math.round(amount * 100)
  return Number.isSafeInteger(cents) ? cents : null
}

export function buildRankingPositionOffers({
  items,
  viewerTotalAmount,
  viewerRank,
  unitPrice,
  minimumQuantity,
  maximumQuantity,
}: {
  items: LandingTopDonor[]
  viewerTotalAmount: string | null
  viewerRank: number | null
  unitPrice: number | string | null
  minimumQuantity: number | null
  maximumQuantity: number | null
}): RankingPositionOffer[] | null {
  const ownCents = moneyToCents(viewerTotalAmount)
  const unitCents = moneyToCents(unitPrice)
  if (
    ownCents == null ||
    unitCents == null ||
    unitCents < 1 ||
    items.some((item) => moneyToCents(item.total_amount) == null)
  )
    return null

  const minimum =
    Number.isInteger(minimumQuantity) && (minimumQuantity ?? 0) > 0 ? minimumQuantity! : 1
  const maximum =
    Number.isInteger(maximumQuantity) && (maximumQuantity ?? 0) > 0
      ? maximumQuantity!
      : null

  return Array.from({ length: 10 }, (_, index): RankingPositionOffer => {
    const rank = index + 1
    const target = items.find((item) => item.rank === rank)
    const targetCents = target ? moneyToCents(target.total_amount) : null
    const already = viewerRank != null && viewerRank <= rank
    const differenceCents =
      targetCents == null || already ? 0 : Math.max(1, targetCents - ownCents + 1)
    const quantity = already
      ? 0
      : Math.max(minimum, Math.ceil(differenceCents / unitCents))
    const amountCents = quantity * unitCents

    return {
      rank,
      occupied: targetCents != null,
      currentAmountCents: targetCents,
      differenceCents,
      quantity,
      amountCents,
      projectedTotalCents: ownCents + amountCents,
      status: already
        ? 'already'
        : maximum != null && quantity > maximum
          ? 'over-limit'
          : 'available',
    }
  })
}
